// src/components/pipelines/nodes/InlineStageConfig.jsx
import React, { useEffect } from 'react';
import { Card } from '../../ui/card.tsx';
import { X } from 'lucide-react';
import { Button } from '../../ui/button.tsx';
import { usePipelines } from '../../../hooks/usePipelines';
import OnEntrySection from '../sheets/OnEntrySection';
import WhileInStageSection from '../sheets/WhileInStageSection';
import OnExitSection from '../sheets/OnExitSection';

/**
 * InlineStageConfig - Inline configuration panel for stage automation
 *
 * Displays horizontally below the kanban board when a stage is being configured.
 * Shows three sections side-by-side:
 * - On Entry: Actions that fire when customer enters the stage
 * - While In Stage: Rules that advance customers to next stage
 * - On Exit: Actions that fire when customer exits the stage
 *
 * Props:
 * - stage: The PipelineStage object being configured
 * - onClose: Callback when panel is closed
 */
const InlineStageConfig = ({ stage, onClose }) => {
  const { rules, rulesLoading, loadRules } = usePipelines();

  // Load rules for this stage when it opens
  useEffect(() => {
    if (stage?.id) {
      console.log('📜 InlineStageConfig: Loading rules for stage:', stage.name);
      loadRules(stage.id);
    }
  }, [stage?.id, loadRules]);

  if (!stage) {
    return null;
  }

  // Filter rules by trigger_type
  const onEntryRules = rules.filter(r => r.stage_id === stage.id && r.trigger_type === 'on_entry');
  const advanceRules = rules.filter(r =>
    r.stage_id === stage.id &&
    ['time_based', 'conditional', 'system_event'].includes(r.trigger_type)
  );
  const onExitRules = rules.filter(r => r.stage_id === stage.id && r.trigger_type === 'on_exit');

  return (
    <div className="mt-4 animate-in slide-in-from-top-2 duration-200">
      <Card className="bg-slate-800/90 border-2 border-purple-500/30 shadow-lg">
        {/* Header */}
        <div className="px-4 py-2 border-b border-slate-700/50 flex items-center justify-between">
          <h3 className="text-sm font-medium text-purple-300">
            {stage.name}
          </h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-6 w-6 p-0 text-slate-400 hover:text-slate-100 hover:bg-slate-700"
          >
            <X className="w-3 h-3" />
          </Button>
        </div>

        {/* Horizontal Sections */}
        <div className="p-3">
          <div className="grid grid-cols-3 gap-3">
            {/* ON ENTRY */}
            <div className="space-y-2">
              <OnEntrySection
                stage={stage}
                rules={onEntryRules}
                isLoading={rulesLoading}
              />
            </div>

            {/* WHILE IN STAGE */}
            <div className="space-y-2 border-x border-slate-700/30 px-3">
              <WhileInStageSection
                stage={stage}
                rules={advanceRules}
                isLoading={rulesLoading}
              />
            </div>

            {/* ON EXIT */}
            <div className="space-y-2">
              <OnExitSection
                stage={stage}
                rules={onExitRules}
                isLoading={rulesLoading}
              />
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default InlineStageConfig;
